import {Api, ApiError, Storage} from "modules/utils";
import {call, delay, put, select, take} from "typed-redux-saga";
import {
	autofillSuccess,
	autoFillTeam,
	autoFillTeamFailed,
	autoFillTeamSuccess,
	IAutoSwap,
	ITeam,
	removeBooster,
	removeBoosterFailed,
	removeBoosterSuccess,
	requestMyTeam,
	requestMyTeamFailed,
	requestMyTeamSuccess,
	requestRivalTeam,
	requestRivalTeamFailed,
	requestRivalTeamSuccess,
	saveBooster,
	saveBoosterFailed,
	saveBoosterSuccess,
	saveTeam,
	saveTeamFailed,
	saveTeamSuccess,
} from "modules/actions/team";
import {
	getActiveEvent,
	getBannerTile,
	getRollbackSeasonOrUndefined,
	getSeason,
	getUser,
	getViewedEvent,
	getViewedTeam,
	isLoggedIn,
} from "modules/selectors";
import {
	fetchFullTeamError,
	fetchFullTeamSuccess,
	hideAutoSwapModal,
	joinTileRequest,
	showAutoSwapModal,
	showBanner,
	showGlobalError,
	showNextSeasonModal,
} from "modules/actions";
import {get, identity, isEmpty, pickBy} from "lodash";
import {BENCH_START_POSITION, LINEUP_SIZE, SEASON_WITH_ROUNDS} from "modules/constants";
import {getViewedRound} from "modules/selectors/rounds";
import {EventStatus, FantasyTileType, RoundStatus} from "modules/enums";
import {ReactComponent as WarningIcon} from "assets/img/icons/warning.svg";

const trackSaveTeam = () =>
	window.dataLayer?.push({
		event: "save_team",
	});

interface IShowAutoSwapModals {
	teamId: number;
	autoswaps: IAutoSwap[];
}

const showAutoSwapModals = function* ({autoswaps, teamId}: IShowAutoSwapModals) {
	const event = yield* select(getActiveEvent);

	if (event?.status === EventStatus.Complete) return;

	const rounds = get(event, "rounds", []);
	const lastCompleteRound = rounds.findLast(({status}) => status === RoundStatus.Complete);

	// show swaps modal only for active or last completed round
	const currentSwaps = autoswaps.filter(({roundId}) => {
		const swapRound = rounds.find(({id}) => id === roundId);

		if (!swapRound) return false;

		if ([RoundStatus.Scheduled, RoundStatus.Active].includes(swapRound.status)) {
			return true;
		}

		return swapRound.id === lastCompleteRound?.id;
	});

	for (const swap of currentSwaps) {
		const storageKey = `autoswap-modal-${teamId}-${swap.id}`;
		const isModalShowed = Storage.GET(storageKey);

		if (!isModalShowed) {
			yield* put(showAutoSwapModal(swap));
			yield* take(hideAutoSwapModal);
			Storage.SET(`autoswap-modal-${teamId}-${swap.id}`, true);
			yield* delay(200);
		}
	}
};

export const showMyTeamSaga = function* ({
	payload: {eventID, roundID},
}: ReturnType<typeof requestMyTeam>) {
	try {
		const selectedRound = yield* select(getSeason);
		const season = yield* select(getRollbackSeasonOrUndefined);

		// Remove season from request if no round season is selected
		const roundId = Number(selectedRound) >= SEASON_WITH_ROUNDS ? roundID : undefined;
		const response = yield* call(Api.FantasyTeam.showMy, eventID, season, roundId);
		const team = response.success.team;

		yield* put(
			requestMyTeamSuccess({
				eventID,
				team,
			})
		);

		const isTeamWithoutCaptain = team.captain === null && Boolean(team.lineup.length);

		if (isTeamWithoutCaptain) {
			yield* put(
				showBanner({
					Icon: WarningIcon,
					title: "Please select a captain!",
					description:
						"Bench player can not be captions, in order to save your team, please select a Captain.",
				})
			);
		}

		if (team.autoswaps?.length) {
			yield* call(showAutoSwapModals, {autoswaps: team.autoswaps, teamId: team.id});
		}
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(requestMyTeamFailed(err));
			yield* put(showGlobalError(err));
		}
	}
};
// TODO update rival team logic
export const showRivalTeamSaga = function* ({payload}: ReturnType<typeof requestRivalTeam>) {
	try {
		const season = yield* select(getRollbackSeasonOrUndefined);
		const response = yield* call(Api.FantasyTeam.showRivalTeam, payload, season);
		yield* put(
			requestRivalTeamSuccess({
				eventID: payload.eventID,
				team: response.success.team,
			})
		);
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(requestRivalTeamFailed(err));
			yield* put(showGlobalError(err));
		}
	}
};

const getTeamData = (team: ITeam | null) => {
	const lineup = team?.players
		.slice(0, BENCH_START_POSITION)
		.map(({playerID}) => playerID)
		.filter((playerID) => playerID !== null);
	const bench = team?.players
		.slice(BENCH_START_POSITION, LINEUP_SIZE)
		.map(({playerID}) => playerID)
		.filter((playerID) => playerID !== null);
	const captain = team?.captain;
	const name = team?.name;

	return {
		lineup: isEmpty(lineup) ? undefined : lineup,
		bench: isEmpty(bench) ? undefined : bench,
		captain,
		name,
	};
};

const showSwingBanner = function* () {
	const tile = yield* select(getBannerTile);

	if (!tile) return;

	const isFullFantasyType = tile.type === FantasyTileType.FULL_FANTASY;
	const title = isFullFantasyType
		? "Play Fantasy DP World Tour Fantasy!"
		: "Ready For Another Challenge?";
	const description = isFullFantasyType
		? "Try the full Fantasy experience here!"
		: `Take part in the ${tile.name} leaderboard`;

	yield* put(
		showBanner({
			image: tile.logo,
			title,
			description,
			redirectUrl: `/team/${tile.id}`,
		})
	);
};

export const saveTeamSaga = function* ({payload: eventID}: ReturnType<typeof saveTeam>) {
	try {
		const team = yield* select(getViewedTeam);
		const round = yield* select(getViewedRound);
		const user = yield* select(getUser);
		const {captain, name, lineup, bench} = getTeamData(team);

		const response = yield* call(
			Api.FantasyTeam.save,
			pickBy(
				{
					name,
					lineup,
					bench,
					captain,
					round: round?.roundNum,
				},
				identity
			)
		);

		const payload = {
			eventID,
			team: response.success.team,
		};

		yield* put(requestMyTeamSuccess(payload));
		yield* put(saveTeamSuccess(payload));

		trackSaveTeam();

		if (!user?.isNextSeasonTermsModalViewed) {
			yield* put(showNextSeasonModal());
		}

		if (!team?.isTeamCreated) {
			yield* call(showSwingBanner);
		}
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(saveTeamFailed(err));
			yield* put(showGlobalError(err));
		}
	}
};

export const saveTeamFromLocalStorageSaga = function* ({
	payload: eventID,
}: ReturnType<typeof saveTeam>) {
	try {
		const team = JSON.parse(sessionStorage.getItem("team") || "null") as ITeam | null;
		const user = yield* select(getUser);
		const {captain, lineup, bench} = getTeamData(team);

		const response = yield* call(
			Api.FantasyTeam.save,
			pickBy(
				{
					lineup,
					bench,
					captain,
				},
				identity
			)
		);

		const payload = {
			eventID,
			team: response.success.team,
		};

		sessionStorage.removeItem("team");

		yield* put(requestMyTeamSuccess(payload));
		yield* put(saveTeamSuccess(payload));

		trackSaveTeam();

		if (!user?.isNextSeasonTermsModalViewed) {
			yield* put(showNextSeasonModal());
		}

		yield* call(showSwingBanner);
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(saveTeamFailed(err));
			yield* put(showGlobalError(err));
		}
	}
};

export const autofillTeamSaga = function* ({payload: eventID}: ReturnType<typeof autoFillTeam>) {
	try {
		const team = yield* select(getViewedTeam);
		const user = yield* select(getUser);
		const isAuthenticated = yield* select(isLoggedIn);
		const {captain, lineup, bench} = getTeamData(team);
		const data = pickBy(
			{
				lineup,
				bench,
				captain,
			},
			identity
		);

		if (isAuthenticated) {
			const response = yield* call(Api.FantasyTeam.autofill, data);

			const payload = {
				eventID,
				team: response.success.team,
			};

			yield* put(requestMyTeamSuccess(payload));
			yield* put(autoFillTeamSuccess(payload));

			if (!user?.isNextSeasonTermsModalViewed) {
				yield* put(showNextSeasonModal());
			}
			// yield* put(requestMyTeamSuccess(payload));
			// yield* put(autoFillTeamSuccess(payload));
		} else {
			const response = yield* call(Api.FantasyTeam.autofill_free, data);

			const payload = {
				eventID,
				team: response.success.team,
			};

			yield* put(autofillSuccess(payload));
		}

		if (!team?.isTeamCreated) {
			yield* call(showSwingBanner);
		}
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(autoFillTeamFailed(err));
			yield* put(showGlobalError(err));
		}
	}
};

export const clearWithdrawnSaga = function* () {
	try {
		yield* call(Api.FantasyTeam.clear_withdrawn);
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(showGlobalError(err));
		}
	}
};

export const showMyTeamFullSaga = function* () {
	try {
		const response = yield* call(Api.FantasyTeam.showMyFull);
		const team = response.success.team;

		yield* put(fetchFullTeamSuccess(team));
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(fetchFullTeamError());
		}
	}
};

export const joinTileSaga = function* ({payload: tileId}: ReturnType<typeof joinTileRequest>) {
	try {
		const response = yield* call(Api.FantasyTeam.joinTile, tileId);
		const team = response.success.team;

		yield* put(fetchFullTeamSuccess(team));
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(fetchFullTeamError());
		}
	}
};

export const saveBoosterSaga = function* ({payload}: ReturnType<typeof saveBooster>) {
	try {
		const event = yield* select(getViewedEvent);
		if (!event) return;

		const response = yield* call(Api.FantasyTeam.saveBooster, payload);

		yield* put(
			saveBoosterSuccess({
				eventID: event.id,
				booster: response.success.booster,
			})
		);
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(saveBoosterFailed());
		}
	}
};

export const removeBoosterSaga = function* ({payload}: ReturnType<typeof removeBooster>) {
	try {
		const event = yield* select(getViewedEvent);
		if (!event) return;

		yield* call(Api.FantasyTeam.removeBooster, payload);

		yield* put(
			removeBoosterSuccess({
				eventID: event.id,
				boosterId: payload,
			})
		);
	} catch (err) {
		if (err instanceof ApiError) {
			yield* put(removeBoosterFailed());
		}
	}
};
